// frontend/src/components/pages/chat/MessageRenderer.jsx
import React, {
  memo,
  useMemo,
  useContext,
  useState,
  createContext,
  useEffect,
  useRef,
} from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import { marked } from "marked";
import {
  Check,
  Copy,
  RefreshCw,
  AlertCircle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import { useTheme } from "../../../context/ThemeContext";
import { isRTL } from "../../../utils/textUtils";
import ImageGenerationShimmer from "./ImageGenerationShimmer";
import CitationsDisplay from "./CitationsDisplay";
import "katex/dist/katex.min.css";

const MarkdownSizeContext = createContext("default");

// Custom Link Component - simplified without preview
function CustomLink({ href, children, ...props }) {
  // Handle click to open in new tab
  const handleClick = (e) => {
    e.preventDefault();
    if (href) {
      window.open(href, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <a
      href={href}
      onClick={handleClick}
      className="text-ac-02 hover:text-ac-01 underline transition-colors"
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    >
      {children}
    </a>
  );
}

function CodeBlock({ children, className, ...props }) {
  const size = useContext(MarkdownSizeContext);
  const { theme } = useTheme(); // 'dark' or 'light'
  const match = /language-(\w+)/.exec(className || "");
  const [copied, setCopied] = useState(false);
  const codeString = String(children);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codeString);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (match) {
    const lang = match[1];
    return (
      <div className="rounded-none relative">
        <div className="flex justify-between items-center px-4 py-2 bg-bg-60 text-foreground rounded-t-md">
          <span className="text-xs font-mono">{lang}</span>
          <button onClick={handleCopy} className="text-sm cursor-pointer">
            {copied ? (
              <Check className="w-4 h-4" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </button>
        </div>
        <SyntaxHighlighter
          language={lang}
          style={theme === "dark" ? oneDark : oneLight}
          customStyle={{
            margin: 0,
            borderRadius: "0 0 8px 8px",
            fontSize: size === "small" ? 12 : 14,
            padding: "1em",
            // Override background color to use your theme
            background: theme === "dark" ? "var(--bg-40)" : "var(--bg-60)",
            // Enable horizontal scrolling for long lines
            overflowX: "auto",
            whiteSpace: "pre",
            wordWrap: "normal",
            maxWidth: "100%",
          }}
          codeTagProps={{
            style: {
              background: "transparent !important",
              whiteSpace: "pre",
              wordWrap: "normal",
            },
          }}
          wrapLines={false}
          wrapLongLines={false}
        >
          {codeString}
        </SyntaxHighlighter>
      </div>
    );
  }

  const inlineCodeClasses =
    size === "small"
      ? "mx-0.5 overflow-auto rounded-md px-1 py-0.5 bg-primary/10 text-foreground font-mono text-xs"
      : "mx-0.5 overflow-auto rounded-md px-2 py-1 bg-primary/10 text-foreground font-mono";

  return (
    <code className={inlineCodeClasses} {...props}>
      {children}
    </code>
  );
}

// Enhanced Image Component with Error Handling
function ImageWithFallback({
  src,
  alt,
  className,
  style,
  onLoad,
  onError,
  ...props
}) {
  const [imageState, setImageState] = useState("loading"); // 'loading', 'loaded', 'error'
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 2;

  const handleImageLoad = () => {
    setImageState("loaded");
    if (onLoad) onLoad();
  };

  const handleImageError = () => {
    if (retryCount < maxRetries) {
      // Retry with a slight delay
      setTimeout(() => {
        setRetryCount((prev) => prev + 1);
        setImageState("loading");
      }, 1000);
    } else {
      setImageState("error");
    }
    if (onError) onError();
  };

  const handleRetry = () => {
    setRetryCount(0);
    setImageState("loading");
  };

  if (imageState === "error") {
    return (
      <div
        className={`flex flex-col items-center justify-center ${className}`}
        style={style}
      >
        <div className="flex flex-col items-center p-4 bg-bg-40 rounded-lg border border-bd-50">
          <AlertCircle className="w-8 h-8 text-fg-60 mb-2" />
          <p className="text-sm text-fg-60 mb-2">Failed to load image</p>
          <button
            onClick={handleRetry}
            className="flex items-center gap-2 px-3 py-1.5 bg-bg-50 hover:bg-bg-30 rounded-md text-xs text-fg-50 transition-colors"
          >
            <RefreshCw className="w-3 h-3" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col items-center ${className}`} style={style}>
      {imageState === "loading" && (
        <div className="flex items-center justify-center p-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-fg-50"></div>
        </div>
      )}
      <img
        src={src}
        alt={alt}
        className={`${
          imageState === "loaded" ? "block" : "hidden"
        } ${className}`}
        style={style}
        onLoad={handleImageLoad}
        onError={handleImageError}
        {...props}
      />
    </div>
  );
}

// Block splitting and rendering
function parseMarkdownIntoBlocks(markdown) {
  if (!markdown) return [];
  const tokens = marked.lexer(markdown);
  return tokens.map((token) => token.raw);
}

function PureMarkdownRendererBlock({ content, onCitationClick }) {
  // Render markdown without inline citation numbers
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkMath]}
      rehypePlugins={[rehypeKatex]}
      components={{
        code: CodeBlock,
        pre: ({ children }) => <>{children}</>,
        a: CustomLink,
      }}
    >
      {content}
    </ReactMarkdown>
  );
}

const MarkdownRendererBlock = memo(
  PureMarkdownRendererBlock,
  (prevProps, nextProps) =>
    prevProps.content === nextProps.content &&
    prevProps.onCitationClick === nextProps.onCitationClick
);

MarkdownRendererBlock.displayName = "MarkdownRendererBlock";

const MessageRenderer = memo(
  ({
    content,
    id,
    size = "default",
    attachments,
    onImageClick,
    isImage,
    imageUrl,
    isLoading,
    imageGenerationStartTime,
    citations,
    reasoning,
    reasoningStreaming,
  }) => {
    const { theme } = useTheme();
    // Use original content without inline citation numbers
    const processedContent = useMemo(() => {
      return content;
    }, [content]);

    // Move hooks to the top, before any conditional logic
    const blocks = useMemo(
      () => parseMarkdownIntoBlocks(processedContent),
      [processedContent]
    );
    const isRtlContent = useMemo(
      () => isRTL(processedContent),
      [processedContent]
    );

    // Check for image attachment after hooks
    const imageAttachment = useMemo(() => {
      if (attachments && attachments.length > 0) {
        return attachments.find((att) => att.type === "image");
      }
      return null;
    }, [attachments]);

    // Collapsible reasoning state
    const [isReasoningExpanded, setIsReasoningExpanded] = useState(
      Boolean(reasoningStreaming)
    );
    useEffect(() => {
      setIsReasoningExpanded(Boolean(reasoningStreaming));
    }, [reasoningStreaming]);

    // Measure overflow for gradient hint when collapsed
    const reasoningRef = useRef(null);
    const [reasoningOverflowing, setReasoningOverflowing] = useState(false);
    useEffect(() => {
      const el = reasoningRef.current;
      if (!el) return;
      if (!isReasoningExpanded) {
        // When collapsed, show gradient only if content exceeds container height
        setReasoningOverflowing(el.scrollHeight > el.clientHeight + 1);
      } else {
        setReasoningOverflowing(false);
      }
    }, [reasoning, isReasoningExpanded]);

    // Handle citation click to scroll to citation
    const handleCitationClick = (citationIndex) => {
      const citationElement = document.querySelector(
        `[data-citation-index="${citationIndex}"]`
      );
      if (citationElement) {
        citationElement.scrollIntoView({ behavior: "smooth", block: "center" });
        // Add a brief highlight effect
        citationElement.classList.add("bg-yellow-200", "dark:bg-yellow-800");
        setTimeout(() => {
          citationElement.classList.remove(
            "bg-yellow-200",
            "dark:bg-yellow-800"
          );
        }, 2000);
      }
    };

    // Handle image generation loading state with shimmer
    if (isImage && isLoading && imageGenerationStartTime) {
      return (
        <div className="flex flex-col items-center">
          <ImageGenerationShimmer
            startTime={imageGenerationStartTime}
            width={512}
            height={512}
            className="max-w-lg"
          />
        </div>
      );
    }

    // Handle completed image generation
    if (isImage && imageUrl) {
      const handleImageClick = () => {
        if (onImageClick) {
          onImageClick(imageUrl, "Generated image", id);
        }
      };

      return (
        <div className="flex flex-col items-center">
          <div onClick={handleImageClick} className="cursor-pointer">
            <ImageWithFallback
              src={imageUrl}
              alt="Generated image"
              className="rounded-lg max-w-lg max-h-[500px] border border-bd-50 cursor-pointer"
              style={{ margin: "0 0" }}
            />
          </div>
        </div>
      );
    }

    // Early return for image attachment rendering
    if (imageAttachment) {
      const handleImageClick = () => {
        if (onImageClick) {
          onImageClick(
            imageAttachment.url,
            imageAttachment.name || "Generated image",
            id
          );
        }
      };

      return (
        <div className="flex flex-col items-center">
          <div onClick={handleImageClick} className="cursor-pointer">
            <ImageWithFallback
              src={imageAttachment.url}
              alt={imageAttachment.name || "Generated image"}
              className="rounded-lg max-w-lg max-h-[500px] border border-bd-50 cursor-pointer"
              style={{ margin: "0 0" }}
            />
          </div>
        </div>
      );
    }

    const proseClasses = [
      size === "small"
        ? "prose prose-sm dark:prose-invert bread-words max-w-none w-full prose-code:before:content-none prose-code:after:content-none break-words"
        : "prose prose-base dark:prose-invert bread-words max-w-none w-full prose-code:before:content-none prose-code:after:content-none break-words",
      isRtlContent ? "text-right" : "",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <MarkdownSizeContext.Provider value={size}>
        <div
          className={proseClasses}
          dir={isRtlContent ? "rtl" : "ltr"}
          style={{ textAlign: isRtlContent ? "right" : "left" }}
        >
          {reasoning && reasoning.trim().length > 0 && (
            <div className="mb-10 p-4 rounded-xl border border-bd-50 border-0.5 bg-bg-60">
              <div className="mb-2 flex items-center justify-between">
                <span>
                  {reasoningStreaming ? "Thinking..." : "Thought Process"}
                </span>
                {!reasoningStreaming && (
                  <button
                    type="button"
                    className="text-fg-50 opacity-80 hover:opacity-100 transition-opacity pb-0.5"
                    onClick={() => setIsReasoningExpanded((v) => !v)}
                    aria-expanded={isReasoningExpanded}
                    aria-label={isReasoningExpanded ? "Collapse" : "Expand"}
                  >
                    {isReasoningExpanded ? (
                      <ChevronUp className="w-5 h-5" />
                    ) : (
                      <ChevronDown className="w-5 h-5" />
                    )}
                  </button>
                )}
              </div>
              <div className="relative">
                <div
                  ref={reasoningRef}
                  className="whitespace-pre-wrap text-[15px] overflow-hidden"
                  style={{
                    maxHeight: isReasoningExpanded ? "1000px" : "5rem",
                    transition: "max-height 300ms ease",
                  }}
                >
                  {reasoning}
                </div>
                {!isReasoningExpanded && !reasoningStreaming && (
                  <div
                    className="pointer-events-none absolute bottom-0 left-0 right-0 h-16"
                    style={{
                      backgroundImage:
                        theme === "dark"
                          ? "linear-gradient(to top, rgba(24,24,24,0.85) 0%, rgba(24,24,24,0.5) 55%, rgba(24,24,24,0) 100%)"
                          : "linear-gradient(to top, rgba(249,249,249,0.85) 0%, rgba(249,249,249,0.5) 55%, rgba(249,249,249,0) 100%)",
                    }}
                  />
                )}
              </div>
            </div>
          )}
          {blocks.map((block, index) => (
            <MarkdownRendererBlock
              content={block}
              onCitationClick={handleCitationClick}
              key={`${id || "msg"}-block-${index}`}
            />
          ))}
          {/* Display citations at the end of the message */}
          {citations && citations.length > 0 && (
            <div className="mt-4">
              <CitationsDisplay citations={citations} />
            </div>
          )}
        </div>
      </MarkdownSizeContext.Provider>
    );
  }
);

MessageRenderer.displayName = "MessageRenderer";

export default MessageRenderer;
